import { useNavigate } from "@tanstack/react-router";
import { ArrowRight, CalendarDays } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import { Checkbox } from "@/components/checkbox";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogTitle,
} from "@/components/dialog";
import { Modal } from "@/components/modal";

import callToActionImage from "./call-to-action.png";

const steps = [
  {
    description: "Plan Instagram and Facebook posts on one calendar.",
    title: "Schedule in one place",
  },
  {
    description: "Views, reach and reactions on every post.",
    title: "See how each post did",
  },
  {
    description: "Find the posts, days and formats that perform best.",
    title: "Learn what's working",
  },
];

interface PublishPromoModalProps {
  isOpen: boolean;
  onDismissForever: () => void;
  onOpenChange: (open: boolean) => void;
}

export function PublishPromoModal({
  isOpen,
  onDismissForever,
  onOpenChange,
}: PublishPromoModalProps) {
  const navigate = useNavigate();
  const [dontAskAgain, setDontAskAgain] = useState(false);

  const close = () => {
    if (dontAskAgain) {
      onDismissForever();
    }
    onOpenChange(false);
  };

  const goToPublish = () => {
    close();
    navigate({ to: "/publish" });
  };

  return (
    <Modal
      classNames={{ modal: "w-full max-w-[980px]" }}
      isDismissable
      isOpen={isOpen}
      onOpenChange={(open) => (open ? onOpenChange(true) : close())}
    >
      <Dialog>
        <DialogCloseButton />
        <DialogBody className="flex flex-col gap-4xl p-3xl md:p-5xl">
          <div className="flex flex-col gap-4xl md:flex-row md:items-center">
            <div className="flex flex-1 flex-col items-start gap-xl">
              <Badge isRounded size="large" variant="purple">
                <Badge.Icon>
                  <CalendarDays />
                </Badge.Icon>
                Publish
              </Badge>
              <DialogTitle className="text-4xl font-bold text-text-primary">
                Take your designs live in minutes.
              </DialogTitle>
              <p className="text-md text-text-tertiary">
                Post your new designs to Facebook or Instagram without leaving
                the platform.
              </p>
              <Button onPress={goToPublish} size="lg" variant="primary">
                Add Publish to my plan
                <ArrowRight className="size-xl" />
              </Button>
            </div>

            <img
              alt=""
              className="w-full min-w-0 flex-1 object-contain"
              src={callToActionImage}
            />
          </div>

          <ol className="grid gap-3xl md:grid-cols-3">
            {steps.map((step, index) => (
              <li
                className="flex flex-col gap-xl rounded-2xl bg-bg-secondary p-3xl"
                key={step.title}
              >
                <span className="flex size-9 items-center justify-center rounded-md bg-bg-brand-solid text-lg font-bold text-text-white">
                  {index + 1}
                </span>
                <div className="flex flex-col gap-md">
                  <p className="text-xl font-bold text-text-primary">
                    {step.title}
                  </p>
                  <p className="text-md text-text-tertiary">
                    {step.description}
                  </p>
                </div>
              </li>
            ))}
          </ol>

          <div className="flex items-center justify-between">
            <Checkbox isSelected={dontAskAgain} onChange={setDontAskAgain}>
              Don&apos;t ask me again
            </Checkbox>
            <Button color="gray" onPress={close} variant="tertiary">
              Maybe later
            </Button>
          </div>
        </DialogBody>
      </Dialog>
    </Modal>
  );
}
